import { UIIcon } from '../../../config/icons';
import { sUpload, YProgressCallback } from '../../mdl/s-upload';
import { $log } from '../../y/debug';
import { $trigger } from '../../y/events';
import { YId } from '../../y/types';
import { UIContent, UIDropDelegate, uiInitDropTarget } from '../dnd';
import { $el } from '../elements';
import { uiMakeFakeUploadInput } from '../functions';
import { UIBaseInput, UIBaseInputOptions } from './input-base';
import { UIProgressWheel } from './progress';

export interface UIMediaInputOptions extends UIBaseInputOptions {
    accept: string[];
    scope: string;
}

export class UIMediaInput extends UIBaseInput<YId | undefined> implements UIDropDelegate {
    file_name?: string;
    private _value?: YId;

    private _scope: string;
    private _accept: string[];
    private _progress: UIProgressWheel;
    private _placeholder: string;
    private _upload_input: HTMLInputElement;

    constructor(options: UIMediaInputOptions) {
        options.r_deco = $el.ICON(UIIcon.ELLIPSIS_STROKE, 'r-arrow');
        const input = $el.DIV();
        super(options, input);
        this.classList.add('ui-select');
        input.tabIndex = 0;

        this._scope = options.scope;
        this._accept = options.accept;
        this._progress = new UIProgressWheel;

        input.addEventListener('blur', () => {
            this.classList.remove('f-focus');
            $trigger(this, 'blur');
        });

        input.addEventListener('focus', () => {
            this.classList.add('f-focus');
            $trigger(this, 'focus');
        });

        this._placeholder = options.placeholder || '\u00a0';
        this._upload_input = uiMakeFakeUploadInput(files => {
            this._onFile(files[0]);
        }, options.accept);

        uiInitDropTarget(this);

        this.root.addEventListener('click', () => { this.openPicker(); });

        this.value = undefined;
    }

    dropEnter(content: UIContent) {
        if(this.disabled) return false;

        this.classList.remove('f-invalid');
        return content.type === 'files';
    }

    drop(content: UIContent) {
        if(this.disabled) return;

        this.classList.remove('f-invalid');
        if(content.type === 'files') {
            const file = content.value[0] as File;
            if(!this._accept.includes(file.type)) return;
            this._onFile(file);
        }
    }

    private async _onFile(file: File) {
        this.setDirty(true);
        this.file_name = undefined;

        const progress: YProgressCallback = p => {
            $log('app', 'progress', p);
            this._progress.progress = p;
        }

        progress(0);
        this.classList.add('f-progress');

        let value: YId | undefined;

        $log('app', 'file', file, file.type);

        try {
            value = (await sUpload(file, this._scope, progress)).id;
            this.file_name = file.name;
        } catch(e) {
            $log('errors', 'error', e);
        }

        this.value = value;
        this.classList.remove('f-progress');
    }

    openPicker() {
        if(this.disabled) return;

        this.input_elem.focus();
        this._upload_input.click();
    }

    override focus() {
        this.input_elem.focus();
    }

    get value() {
        return this._value;
    }

    set value(value: YId | undefined) {
        if(!value)
            value = undefined;

        this._value = value;

        this.fixText();
        this.doCheck();
        this.fixDesign();

        $trigger(this, 'value', this._value);
    }

    get is_empty() {
        return !this._value;
    }

    protected fixText() {
        const v = this._value;

        let text: string;

        if(!v) {
            this.input_elem.classList.add('f-placeholder');
            text = this._placeholder;
        } else {
            this.input_elem.classList.remove('f-placeholder');
            text = v;
        }

        this.input_elem.textContent = text || '\u00a0';
    }

    protected doCheck() {
        this.setOkay(!this.required || !this.is_empty);
    }
}
